// **********************************************************************************
// * Copied from experience-journey-apps/packages/ui/src/components/Pagination
// *
// * Simplified for Experiences-UI.
// * Biggest difference is the lack of a `defaultPage` prop in Experiences-UI.
// * Experiences-UI pagination is always controlled by the `page` and `onPageChange` props.
// * This is compatible with Core component Pagination API.
// *
// * This pagination also doesn't allow for NextLink and AnchorLink types.
// * Items are all Buttons, as they were in the "old" Pagination
// *
// * This component can/should be replaced by Core UI's Pagination when possible.
// **********************************************************************************

import styled from '@emotion/styled';
import { Flex } from '@qga/roo-ui/components';

import ItemCount from './components/ItemCount';
import PaginationItem from './components/PaginationItem';
import { ELLIPSIS_MARKER, getNumberOfPages, getPaginationItems } from './utils';

const Ellipsis = styled.li`
  display: flex;
  align-items: end;

  span {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
  }
`;

const List = styled.ul`
  list-style: none;
  display: flex;
  gap: 10px;
  padding: 0;
  margin: 0;
  font-weight: 700;
`;

const ListItem = styled.li`
  display: inline-block;
  text-align: center;
`;

const DEFAULT_PAGE = 1;

export interface PaginationProps {
  className?: string;
  itemName?: string;
  itemsPerPage?: number;
  numItems?: number;
  numPages?: number;
  page: number;
  showItemCount?: boolean;
  onPageChange: (pageNumber: number) => void;
}

const Pagination = ({
  className,
  numPages: numPagesProp,
  page,
  onPageChange,
  showItemCount,
  numItems,
  itemsPerPage,
  itemName,
  ...props
}: PaginationProps) => {
  const numPages = numPagesProp ?? getNumberOfPages({ numItems, itemsPerPage });
  const currentPage = Math.max(1, Math.min(page ?? DEFAULT_PAGE, numPages));

  const paginationItems = getPaginationItems({
    currentPage,
    numPages,
  });
  const nextPage = currentPage + 1;
  const prevPage = currentPage - 1;

  const isOnFirstPage = currentPage === 1;
  const isOnLastPage = currentPage === numPages;

  return (
    <Flex justifyContent="center" flexDirection="column" alignItems="center">
      <nav aria-label="pagination" className={className}>
        <List>
          <ListItem>
            <PaginationItem
              aria-label="Previous page"
              disabled={isOnFirstPage}
              itemVariant="previous"
              page={prevPage}
              onItemChange={() => {
                onPageChange(prevPage);
              }}
              {...props}
            />
          </ListItem>
          {paginationItems.map((item, index) => {
            if (item === ELLIPSIS_MARKER) {
              return (
                <Ellipsis key={index}>
                  <span>&#8230;</span>
                </Ellipsis>
              );
            }

            return (
              <ListItem key={index}>
                <PaginationItem
                  aria-label={`Page ${item}`}
                  aria-current={currentPage === item}
                  disabled={false}
                  isSelected={currentPage === item}
                  page={item}
                  itemVariant="page"
                  onItemChange={() => {
                    onPageChange(item);
                  }}
                  {...props}
                />
              </ListItem>
            );
          })}
          <ListItem>
            <PaginationItem
              aria-label="Next page"
              disabled={isOnLastPage}
              itemVariant="next"
              page={nextPage}
              onItemChange={() => {
                onPageChange(nextPage);
              }}
              {...props}
            />
          </ListItem>
        </List>
      </nav>
      {showItemCount && (
        <ItemCount
          itemName={itemName}
          itemsPerPage={itemsPerPage}
          numItems={numItems}
          currentPage={currentPage}
        />
      )}
    </Flex>
  );
};

export default Pagination;
